import { useEffect, useMemo, useState } from "react";
import { marked } from "marked";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

marked.setOptions({ gfm: true, breaks: true });

const DOC_TYPES = ["90_day", "1_year", "gtm", "playbook", "strategy", "prd", "custom"];

function DocPreview({ md }) {
  const html = useMemo(() => marked.parse(md || ""), [md]);
  return (
    <div
      className="tb-doc-preview"
      dangerouslySetInnerHTML={{ __html: html }}
      data-testid="doc-preview"
    />
  );
}

function SignoffBadge({ doc, t }) {
  const required = doc.required_signoffs || [];
  const signed = doc.signoffs || [];
  const signedIds = new Set(signed.map((s) => s.user_id));
  const missing = required.filter((r) => !signedIds.has(r.user_id));
  if (doc.status === "official") {
    return <span className="tb-badge good" data-testid="doc-status-official">{t("docs.statusOfficial")}</span>;
  }
  if (required.length === 0) {
    return <span className="tb-badge" data-testid="doc-status-draft">{t("docs.statusDraft")}</span>;
  }
  return (
    <span
      className={`tb-badge ${missing.length === 0 ? "good" : "warn"}`}
      data-testid="doc-status-pending"
      title={missing.map((m) => m.name).join(", ")}
    >
      {t("docs.signedCount", { signed: signed.length, total: required.length })}
    </span>
  );
}

function DocEditor({ doc, onSave, onArchive, onDelete, onPublish, onSignoff, onRequestSignoff, onChat, allUsers, t }) {
  const { user } = useAuth();
  const [title, setTitle] = useState(doc.title);
  const [content, setContent] = useState(doc.content_md);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [showVersions, setShowVersions] = useState(false);
  const [showSignoff, setShowSignoff] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [chatQ, setChatQ] = useState("");
  const [chatA, setChatA] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [selectedSigners, setSelectedSigners] = useState([]);
  const [signComment, setSignComment] = useState("");

  useEffect(() => {
    setTitle(doc.title);
    setContent(doc.content_md);
    setNote("");
    setChatA("");
    setSelectedSigners((doc.required_signoffs || []).map((r) => r.user_id));
  }, [doc.id, doc.updated_at, doc.title, doc.content_md, doc.required_signoffs]);

  const dirty = title !== doc.title || content !== doc.content_md;

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await onSave({ title, content_md: content, note });
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("docs.errorSave"));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    try {
      await onPublish();
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("docs.errorPublish"));
    }
  };

  const signoff = async () => {
    try {
      await onSignoff({ comment: signComment });
      setSignComment("");
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("docs.errorSignoff"));
    }
  };

  const requestSignoffs = async () => {
    try {
      await onRequestSignoff(selectedSigners);
      setShowSignoff(false);
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("docs.errorSignoff"));
    }
  };

  const askDoc = async () => {
    if (!chatQ.trim()) return;
    setChatBusy(true);
    setChatA("");
    try {
      const r = await onChat(chatQ);
      setChatA(r.answer || "");
    } catch (e) {
      setChatA(formatApiErrorDetail(e) || t("docs.errorChat"));
    } finally {
      setChatBusy(false);
    }
  };

  const requiredIds = new Set((doc.required_signoffs || []).map((r) => r.user_id));
  const hasSigned = (doc.signoffs || []).some((s) => s.user_id === user?.id);

  return (
    <div className="tb-doc-editor" data-testid={`doc-editor-${doc.id}`}>
      <header className="tb-doc-editor-head">
        <input
          className="tb-doc-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid="doc-title-input"
        />
        <div className="tb-doc-actions">
          <SignoffBadge doc={doc} t={t} />
          <button type="button" className="tb-ghost-btn tb-mini" onClick={() => setShowVersions((v) => !v)} data-testid="doc-versions-btn" title={t("docs.tip.version")}>
            v{doc.version}
          </button>
          <button type="button" className="tb-ghost-btn tb-mini" onClick={() => setShowChat((v) => !v)} data-testid="doc-chat-btn" title={t("docs.tip.askDoc")}>
            {t("docs.chatBtn")}
          </button>
          <button type="button" className="tb-ghost-btn tb-mini" onClick={() => setShowSignoff((v) => !v)} data-testid="doc-signoff-btn" title={t("docs.tip.signoffs")}>
            {t("docs.signoffBtn")}
          </button>
          <button type="button" className="tb-ghost-btn tb-mini" onClick={onArchive} data-testid="doc-archive-btn" title={t("docs.tip.archive")}>
            {t("docs.archive")}
          </button>
          {(doc.owner_id === user?.id || user?.role === "admin") && (
            <button
              type="button"
              className="tb-ghost-btn tb-mini tb-danger"
              onClick={onDelete}
              data-testid="doc-delete-btn"
              title={t("docs.tip.delete")}
            >
              {t("docs.delete")}
            </button>
          )}
          {doc.status !== "official" && (
            <button type="button" className="tb-primary-btn tb-mini" onClick={publish} data-testid="doc-publish-btn" title={t("docs.tip.publish")}>
              {t("docs.publish")}
            </button>
          )}
          <button
            type="button"
            className="tb-primary-btn tb-mini"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="doc-save-btn"
            title={t("docs.tip.save")}
          >
            {saving ? t("docs.saving") : t("docs.save")}
          </button>
          <button
            type="button"
            className="tb-icon-btn tb-mini"
            onClick={() => setShowHelp((v) => !v)}
            data-testid="doc-help-btn"
            title={t("docs.tip.help")}
            aria-label={t("docs.tip.help")}
          >
            ?
          </button>
        </div>
      </header>
      {err && <div className="tb-error">{err}</div>}

      {showHelp && (
        <section className="tb-doc-help" data-testid="doc-help-panel">
          <header className="tb-doc-help-head">
            <h3 style={{ margin: 0 }}>{t("docs.help.title")}</h3>
            <button type="button" className="tb-icon-btn tb-mini" onClick={() => setShowHelp(false)} aria-label="Close">×</button>
          </header>
          <div className="tb-doc-help-body">
            <h4>{t("docs.help.pillsTitle")}</h4>
            <ul className="tb-doc-help-list">
              <li><span className="tb-badge">{t("docs.statusDraft")}</span> {t("docs.help.pill.draft")}</li>
              <li><span className="tb-badge good">{t("docs.statusOfficial")}</span> {t("docs.help.pill.official")}</li>
              <li><span className="tb-badge warn">2/3</span> {t("docs.help.pill.pending")}</li>
              <li><strong>v3</strong> — {t("docs.help.pill.version")}</li>
              <li><strong>{t("docs.chatBtn")}</strong> — {t("docs.help.pill.askDoc")}</li>
              <li><strong>{t("docs.signoffBtn")}</strong> — {t("docs.help.pill.signoffs")}</li>
              <li><strong>{t("docs.archive")}</strong> — {t("docs.help.pill.archive")}</li>
              <li><strong>{t("docs.delete")}</strong> — {t("docs.help.pill.delete")}</li>
              <li><strong>{t("docs.publish")}</strong> — {t("docs.help.pill.publish")}</li>
              <li><strong>{t("docs.save")}</strong> — {t("docs.help.pill.save")}</li>
            </ul>
            <h4 style={{ marginTop: 16 }}>{t("docs.help.catsTitle")}</h4>
            <ul className="tb-doc-help-list">
              {DOC_TYPES.map((k) => (
                <li key={k}>
                  <strong>{t(`docs.types.${k}`)}</strong> — {t(`docs.help.cat.${k}`)}
                </li>
              ))}
            </ul>
            <p className="tb-muted" style={{ marginTop: 12 }}>{t("docs.help.vettingIntro")}</p>
          </div>
        </section>
      )}

      <input
        type="text"
        className="tb-doc-note-input"
        placeholder={t("docs.versionNotePlaceholder")}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        data-testid="doc-note-input"
      />

      <div className="tb-doc-workspace">
        <textarea
          className="tb-doc-textarea"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          spellCheck={false}
          data-testid="doc-textarea"
        />
        <DocPreview md={content} />
      </div>

      {showVersions && (
        <section className="tb-doc-versions" data-testid="doc-versions">
          <h3>{t("docs.versionHistory")}</h3>
          <ul>
            {(doc.versions || []).slice().reverse().map((v) => (
              <li key={v.version}>
                <strong>v{v.version}</strong>
                <span className="tb-muted"> · {new Date(v.updated_at).toLocaleString()} · {v.updated_by_name}</span>
                {v.note && <span className="tb-muted"> · &ldquo;{v.note}&rdquo;</span>}
                <button
                  type="button"
                  className="tb-link"
                  onClick={() => {
                    if (window.confirm(t("docs.restoreConfirm"))) {
                      setContent(v.content_md);
                      setTitle(v.title || title);
                    }
                  }}
                  data-testid={`doc-restore-v${v.version}`}
                >
                  {t("docs.restore")}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {showSignoff && (
        <section className="tb-doc-signoff" data-testid="doc-signoff">
          <h3>{t("docs.signoffTitle")}</h3>
          <p className="tb-muted">{t("docs.signoffIntro")}</p>
          <div className="tb-doc-signoff-list">
            {allUsers.filter((u) => u.role === "entrepreneur" || u.role === "advisor").map((u) => (
              <label key={u.id} className="tb-checkbox">
                <input
                  type="checkbox"
                  checked={selectedSigners.includes(u.id)}
                  onChange={(e) => {
                    setSelectedSigners((prev) =>
                      e.target.checked ? [...prev, u.id] : prev.filter((x) => x !== u.id),
                    );
                  }}
                  data-testid={`doc-signer-${u.id}`}
                />
                {u.name || u.email} <span className="tb-muted">({u.role})</span>
              </label>
            ))}
          </div>
          <button
            type="button"
            className="tb-primary-btn tb-mini"
            onClick={requestSignoffs}
            data-testid="doc-req-signoff-submit"
          >
            {t("docs.requestSignoff")}
          </button>

          {(doc.signoffs || []).length > 0 && (
            <div className="tb-doc-signoff-signed">
              <h4>{t("docs.signedBy")}</h4>
              <ul>
                {doc.signoffs.map((s) => (
                  <li key={s.user_id}>
                    ✓ <strong>{s.name}</strong>
                    <span className="tb-muted"> · {new Date(s.signed_at).toLocaleString()}</span>
                    {s.comment && <p className="tb-mem-notes">&ldquo;{s.comment}&rdquo;</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(requiredIds.has(user?.id) || doc.owner_id === user?.id) && !hasSigned && (
            <div className="tb-doc-sign-me">
              <input
                type="text"
                value={signComment}
                onChange={(e) => setSignComment(e.target.value)}
                placeholder={t("docs.signCommentPh")}
                data-testid="doc-sign-comment"
              />
              <button type="button" className="tb-primary-btn tb-mini" onClick={signoff} data-testid="doc-sign-submit">
                {t("docs.signNow")}
              </button>
            </div>
          )}
        </section>
      )}

      {showChat && (
        <section className="tb-doc-chat" data-testid="doc-chat-panel">
          <h3>{t("docs.chatTitle")}</h3>
          <div className="tb-doc-chat-row">
            <input
              type="text"
              value={chatQ}
              onChange={(e) => setChatQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") askDoc(); }}
              placeholder={t("docs.chatPlaceholder")}
              data-testid="doc-chat-input"
            />
            <button
              type="button"
              className="tb-primary-btn tb-mini"
              onClick={askDoc}
              disabled={chatBusy || !chatQ.trim()}
              data-testid="doc-chat-send"
            >
              {chatBusy ? "…" : t("docs.ask")}
            </button>
          </div>
          {chatA && (
            <div className="tb-doc-chat-answer" data-testid="doc-chat-answer">
              {chatA.split("\n").map((l, i) => <p key={i}>{l}</p>)}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function NewDocForm({ onCreate, onCancel, t }) {
  const [type, setType] = useState("90_day");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await onCreate({ type, title: title.trim(), content_md: `# ${title.trim()}\n\n` });
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("docs.errorCreate"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tb-modal-backdrop" data-testid="doc-new-modal">
      <div className="tb-modal">
        <header className="tb-modal-head">
          <h3>{t("docs.newTitle")}</h3>
          <button type="button" className="tb-icon-btn" onClick={onCancel}>×</button>
        </header>
        <div className="tb-modal-body">
          <label>
            {t("docs.type")}
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="doc-new-type">
              {DOC_TYPES.map((k) => (
                <option key={k} value={k}>{t(`docs.types.${k}`)}</option>
              ))}
            </select>
          </label>
          <label style={{ display: "block", marginTop: 12 }}>
            {t("docs.docTitle")}
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              data-testid="doc-new-title"
            />
          </label>
          {err && <div className="tb-error" style={{ marginTop: 8 }}>{err}</div>}
        </div>
        <footer className="tb-modal-foot">
          <button type="button" className="tb-ghost-btn" onClick={onCancel}>{t("docs.cancel")}</button>
          <button
            type="button"
            className="tb-primary-btn"
            onClick={submit}
            disabled={busy || !title.trim()}
            data-testid="doc-new-submit"
          >
            {busy ? t("docs.creating") : t("docs.create")}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function Docs() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  const canView = user && (user.role === "admin" || user.features?.journal_enabled === true);

  const loadDocs = async () => {
    try {
      const r = await api.get("/docs");
      setDocs(r.data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e) || "");
    }
  };

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    (async () => {
      await loadDocs();
      try {
        const r = await api.get("/team");
        const d = r.data || {};
        const owner = d.owner ? [d.owner] : [];
        const members = Array.isArray(d.members) ? d.members : (Array.isArray(d.items) ? d.items : []);
        setAllUsers([...owner, ...members]);
      } catch { /* non-fatal */ }
      setLoading(false);
    })();
  }, [canView]);

  const loadDetail = async (id) => {
    try {
      const r = await api.get(`/docs/${id}`);
      setSelected(r.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e) || "");
    }
  };

  const createDoc = async ({ type, title, content_md }) => {
    const r = await api.post("/docs", { type, title, content_md });
    setShowNew(false);
    await loadDocs();
    setSelected(r.data);
  };

  const seedStarterPack = async () => {
    try {
      await api.post("/docs/seed-starter-pack", { lang: user?.lang || "en" });
      await loadDocs();
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("docs.errorSeed"));
    }
  };

  const saveDoc = async ({ title, content_md, note }) => {
    const r = await api.put(`/docs/${selected.id}`, { title, content_md, note });
    setSelected(r.data);
    await loadDocs();
  };

  const archiveDoc = async () => {
    if (!window.confirm(t("docs.archiveConfirm"))) return;
    await api.delete(`/docs/${selected.id}`);
    setSelected(null);
    await loadDocs();
  };

  const deleteDoc = async () => {
    if (!window.confirm(t("docs.deleteConfirm"))) return;
    try {
      await api.delete(`/docs/${selected.id}/hard`);
      setSelected(null);
      await loadDocs();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const publish = async () => {
    const r = await api.post(`/docs/${selected.id}/publish`);
    setSelected(r.data);
    await loadDocs();
  };

  const signoff = async ({ comment }) => {
    const r = await api.post(`/docs/${selected.id}/signoff`, { comment });
    setSelected(r.data);
  };

  const requestSignoff = async (user_ids) => {
    const r = await api.post(`/docs/${selected.id}/request-signoff`, { user_ids });
    setSelected(r.data);
  };

  const chatWithDoc = async (question) => {
    const r = await api.post(`/docs/${selected.id}/chat`, { question, lang: user?.lang || "en" });
    return r.data;
  };

  if (!canView) {
    return (
      <div className="tb-page tb-docs-page" data-testid="docs-page">
        <header className="tb-mem-header">
          <div>
            <h1>{t("docs.title")}</h1>
            <p className="tb-muted">{t("docs.subtitle")}</p>
          </div>
        </header>
        <div className="tb-card"><p className="tb-muted">{t("proposals.emptyForRole")}</p></div>
      </div>
    );
  }

  return (
    <div className="tb-page tb-docs-page" data-testid="docs-page">
      <header className="tb-mem-header">
        <div>
          <h1>{t("docs.title")}</h1>
          <p className="tb-muted">{t("docs.subtitle")}</p>
        </div>
        <div className="tb-doc-header-actions">
          <button
            type="button"
            className="tb-ghost-btn"
            onClick={seedStarterPack}
            data-testid="docs-seed-btn"
            title={t("docs.seedPackHint")}
          >
            {t("docs.seedPack")}
          </button>
          <button
            type="button"
            className="tb-primary-btn"
            onClick={() => setShowNew(true)}
            data-testid="docs-new-btn"
          >
            + {t("docs.new")}
          </button>
        </div>
      </header>

      {err && <div className="tb-error">{err}</div>}

      {loading ? (
        <p className="tb-muted">{t("docs.loading")}</p>
      ) : (
        <div className="tb-docs-layout">
          <aside className="tb-docs-sidebar" data-testid="docs-sidebar">
            {docs.length === 0 ? (
              <div className="tb-docs-empty">
                <p className="tb-muted">{t("docs.emptyBig")}</p>
                <button
                  type="button"
                  className="tb-primary-btn"
                  onClick={seedStarterPack}
                  data-testid="docs-seed-empty-btn"
                >
                  {t("docs.loadStarterPack")}
                </button>
                <p className="tb-muted tb-tiny">{t("docs.seedPackHint")}</p>
              </div>
            ) : (
              <ul>
                {docs.map((d) => (
                  <li
                    key={d.id}
                    className={selected?.id === d.id ? "active" : ""}
                    onClick={() => loadDetail(d.id)}
                    data-testid={`docs-item-${d.id}`}
                  >
                    <strong>{d.title}</strong>
                    <div className="tb-muted tb-doc-item-meta">
                      {t(`docs.types.${d.type}`)}
                      <span> · v{d.version}</span>
                      <span> · {d.status === "official" ? t("docs.statusOfficial") : t("docs.statusDraft")}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </aside>
          <div className="tb-docs-main">
            {selected ? (
              <DocEditor
                doc={selected}
                onSave={saveDoc}
                onArchive={archiveDoc}
                onDelete={deleteDoc}
                onPublish={publish}
                onSignoff={signoff}
                onRequestSignoff={requestSignoff}
                onChat={chatWithDoc}
                allUsers={allUsers}
                t={t}
              />
            ) : (
              <div className="tb-card">
                <p className="tb-muted">{t("docs.selectHint")}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showNew && <NewDocForm onCreate={createDoc} onCancel={() => setShowNew(false)} t={t} />}
    </div>
  );
}
